/**
 * MeleeConeProfile — resolves the SHAPE of a melee swing (reach, arc, target
 * cap) from canonical TOML, shared by client prediction and server authority.
 *
 * `COMBAT-MELEE-CONE-02` (2026-07-26, DJ): a greatsword and a dagger must not
 * sweep the same wedge. Warrior/paladin get broad cleaves; a rogue gets a
 * NARROW arc with a low target cap — shape carries the fantasy, reach alone
 * does not.
 *
 * WHY THE DATA LIVES IN TOML (ratified 2026-07-26, do not re-open):
 * a class→shape map in TypeScript is exactly the drift shape that bit
 * `AttackRange` twice — `RANGED_HERO_CLASSES` disagreed with the ability tags
 * in BOTH directions until it was derived from TOML instead (commit
 * `5eb32b8f`). It also cannot express what DJ asked for, which is per-ABILITY
 * variation, not only per-class.
 *
 * WHY IT IS SHARED AND PURE: identical to `MeleeCone.ts` itself. The client
 * predicts which enemies a swing hits and the server decides what actually
 * takes damage. Two implementations of the shape would desync — the client
 * would flash damage numbers on enemies the server never hit.
 *
 * Resolution order, PER FIELD, first hit wins:
 *
 *     ability TOML  →  class default  →  GAMEPLAY_CONFIG global
 *
 * Per-field (not whole-profile) so an ability can narrow only its arc and
 * inherit reach. The global is the FLOOR: with no TOML cone fields anywhere,
 * every resolution returns exactly today's global cone, which is what makes
 * this a purely additive change.
 *
 * The "class default" is itself TOML — the cone declared on that class's
 * `basic`-tagged ability (its auto-attack). There is no hand-written class
 * table anywhere in this file, by design. `meleeConeClassSources()` exposes
 * which abilities fed the class layer so a test can fail on ambiguity instead
 * of silently picking one.
 */

import { GAMEPLAY_CONFIG } from '../constants/GameplayConfig';
import { ABILITY_DATA, getAbilityData } from '../content/AbilityDataAdapter';
import type { ConeSpec } from './MeleeCone';
import { meleeSwingHalfAngles } from '../config/melee-swing-geometry.config';

/** A fully resolved cone: every field present, ready to hand to `MeleeCone`. */
export interface MeleeConeProfile extends ConeSpec {
  /** Bodies one swing carries through. 0 = uncapped (see resolveConeTargets). */
  maxTargets: number;
  /** Plan 29 D55: degrees above facing the swing hits (flat swing + margin). */
  upDeg?: number;
  /** Plan 29 D55: degrees below facing the swing hits. */
  downDeg?: number;
}

/** A partial cone as AUTHORED — any subset of the three knobs. */
export interface DeclaredCone {
  rangePx?: number;
  arcDeg?: number;
  maxTargets?: number;
}

/** The global floor, in the shape `GAMEPLAY_CONFIG` stores it. */
export interface MeleeConeConfig {
  meleeConeRangePx: number;
  meleeConeArcDeg: number;
  meleeConeMaxTargets: number;
}

/**
 * Anti-cheat forgiveness on the DISTANCE knobs, mirroring the AoE radius clamp
 * in `CombatDamageHandler` (canonical × 1.2). A count has no jitter, so
 * `maxTargets` is clamped exactly.
 */
export const CONE_CLAMP_TOLERANCE = 1.2;

/** Positive finite numbers only — a 0/NaN/negative authored value is no value. */
function positive(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

function coneFieldsOf(data: unknown): DeclaredCone {
  const d = data as { coneRangePx?: number; coneArcDeg?: number; coneMaxTargets?: number } | undefined;
  if (!d) return {};
  return {
    rangePx: positive(d.coneRangePx),
    arcDeg: positive(d.coneArcDeg),
    maxTargets: positive(d.coneMaxTargets),
  };
}

/** The cone fields declared on this exact ability (no fallbacks applied). */
export function declaredConeForAbility(abilityId?: string | null): DeclaredCone {
  if (!abilityId) return {};
  return coneFieldsOf(getAbilityData(abilityId));
}

/**
 * class → the cone declared on its `basic`-tagged ability, derived once from
 * the generated TOML. Records EVERY declaring source per class so ambiguity is
 * testable rather than order-dependent.
 */
let classCones: Map<string, { cone: DeclaredCone; sourceIds: string[] }> | null = null;

function buildClassCones(): Map<string, { cone: DeclaredCone; sourceIds: string[] }> {
  const map = new Map<string, { cone: DeclaredCone; sourceIds: string[] }>();
  for (const [id, ability] of Object.entries(ABILITY_DATA)) {
    const { class: heroClass, tags } = ability as { class?: string; tags?: string[] };
    if (!heroClass || !tags?.includes('basic')) continue;
    const cone = coneFieldsOf(ability);
    if (cone.rangePx === undefined && cone.arcDeg === undefined && cone.maxTargets === undefined) continue;
    const existing = map.get(heroClass);
    if (existing) {
      existing.sourceIds.push(id);
      continue;
    }
    map.set(heroClass, { cone, sourceIds: [id] });
  }
  return map;
}

/** The class-layer cone (from that class's basic ability), if any. */
export function declaredConeForClass(heroClass?: string | null): DeclaredCone {
  if (!heroClass) return {};
  classCones ??= buildClassCones();
  return classCones.get(heroClass)?.cone ?? {};
}

/**
 * Which ability ids fed each class's cone default. More than one entry means
 * two `basic` abilities of the same class both declare a cone and the class
 * layer is ambiguous — `MeleeConeProfile.test.ts` fails on that rather than
 * letting object-key order decide the feel of a class.
 */
export function meleeConeClassSources(): Record<string, string[]> {
  classCones ??= buildClassCones();
  const out: Record<string, string[]> = {};
  for (const [heroClass, entry] of classCones) out[heroClass] = [...entry.sourceIds];
  return out;
}

/** Test-only: drop the memoised class index (the generated data is static). */
export function __resetMeleeConeCache(): void {
  classCones = null;
}

/**
 * The cone an attacker actually swings: ability → class → global, per field.
 *
 * `config` exists for tests / balance experiments, exactly like
 * `getAttackRange`; production always passes the real `GAMEPLAY_CONFIG`.
 */
export function resolveMeleeCone(
  abilityId?: string | null,
  heroClass?: string | null,
  config: MeleeConeConfig = GAMEPLAY_CONFIG,
): MeleeConeProfile {
  const ability = declaredConeForAbility(abilityId);
  const klass = declaredConeForClass(heroClass);
  const arcDeg = ability.arcDeg ?? klass.arcDeg ?? config.meleeConeArcDeg;
  const vertical = meleeSwingHalfAngles(arcDeg);
  return {
    // Plan 29 D55: a melee ability with no cone reach of its own reaches as far
    // as its cast gate lets it (TOML `range`, the distance the client refuses
    // past), not the class auto's cone — so the swing drawn for Mortal Strike
    // ends where Mortal Strike stops landing.
    rangePx: ability.rangePx ?? meleeAbilityRange(abilityId) ?? klass.rangePx ?? config.meleeConeRangePx,
    arcDeg,
    maxTargets: ability.maxTargets ?? klass.maxTargets ?? config.meleeConeMaxTargets,
    ...(vertical ?? {}),
  };
}

/**
 * Plan 29 D55 — the vertical spread the SERVER's area scan applies to a cone
 * payload, from the ability it names: a `melee`-tagged ability (or a class
 * auto, which is how the melee sweep reports itself) swings the flat
 * swing-plus-margin wedge for the arc it swung; any other cone (a breath, a
 * howl) keeps its symmetric arc. Undefined = symmetric. Derived from the
 * ability id rather than sent on the wire, so an older client keeps working.
 */
export function meleeSwingVertical(abilityId: string | null | undefined, arcDeg: number): { upDeg: number; downDeg: number } | undefined {
  if (!abilityId) return undefined;
  const data = getAbilityData(abilityId) as { tags?: string[] } | undefined;
  if (!data?.tags?.includes('melee')) return undefined;
  return meleeSwingHalfAngles(arcDeg) ?? undefined;
}

/** TOML `range` of a `melee`-tagged ability that declares no cone reach. */
function meleeAbilityRange(abilityId?: string | null): number | undefined {
  if (!abilityId) return undefined;
  const data = getAbilityData(abilityId) as { range?: number; tags?: string[] } | undefined;
  if (!data?.tags?.includes('melee')) return undefined;
  return positive(data.range);
}

/** Server-side ceilings for a client-claimed cone. `Infinity` = no ceiling. */
export interface ConeAntiCheatCaps {
  rangeCap: number;
  arcCap: number;
  maxTargets: number;
}

/**
 * The ceilings the server enforces on a client-sent cone, derived from the
 * ability the client CLAIMS to be casting.
 *
 * ABILITY-LEVEL ONLY — deliberately NOT the resolved profile:
 *  - Falling back to the class layer would narrow that class's other cone
 *    abilities. Rogue `death_sweep` legitimately sweeps 120° while
 *    `assassin_auto` is 55°; a class-wide ceiling would silently shrink it.
 *  - Falling back to the GLOBAL would be worse still: every pre-existing
 *    `shape: 'cone'` ability (carve/sweeping_strikes/death_sweep at 120°)
 *    would be clamped to the 100° global the moment this shipped.
 * An ability that declares nothing is therefore uncapped HERE and stays bound
 * by `antiCheat.maxAreaRadius` as before — the same contract the canonical
 * radius clamp already follows.
 */
export function coneAntiCheatCaps(abilityId?: string | null): ConeAntiCheatCaps {
  const declared = declaredConeForAbility(abilityId);
  return {
    rangeCap: declared.rangePx === undefined ? Infinity : declared.rangePx * CONE_CLAMP_TOLERANCE,
    arcCap: declared.arcDeg === undefined ? Infinity : declared.arcDeg * CONE_CLAMP_TOLERANCE,
    maxTargets: declared.maxTargets ?? Infinity,
  };
}
